import React, { useState, useEffect, useRef } from "react";
import { Award, X } from "lucide-react";
import { SponsorData, SponsorTypeData } from "../../CreateQickEventV2/types";
import ImageCropper from "../../../../components/common/ImageCropper/ImageCropper";

interface NewSponsorModalV3Props {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (sponsor: SponsorData) => void;
  editingSponsor?: SponsorData | null;
  sponsorTypes?: SponsorTypeData[];
  isLoading?: boolean;
}

const DEFAULT_SPONSOR_TYPES: SponsorTypeData[] = [
  { sponsorTypeId: 1, title: "Title Sponsor", isActive: true },
  { sponsorTypeId: 2, title: "Gold Sponsor", isActive: true },
  { sponsorTypeId: 3, title: "Silver Sponsor", isActive: true },
  { sponsorTypeId: 4, title: "Bronze Sponsor", isActive: true },
  { sponsorTypeId: 5, title: "Media Partner", isActive: true },
];

const NewSponsorModalV3: React.FC<NewSponsorModalV3Props> = ({
  isOpen,
  toggle,
  onSubmit,
  editingSponsor = null,
  sponsorTypes = DEFAULT_SPONSOR_TYPES,
  isLoading = false,
}) => {
  const [sponsorTypeId, setSponsorTypeId] = useState<string>("");
  const [name, setName] = useState("");
  const [link, setLink] = useState("");

  const [thumb, setThumb] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [imageError, setImageError] = useState("");
  const previewUrlRef = useRef<string | null>(null);

  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [fileToCrop, setFileToCrop] = useState<File | null>(null);
  const [validationError, setValidationError] = useState("");

  const activeSponsorTypes = sponsorTypes && sponsorTypes.length > 0 ? sponsorTypes : DEFAULT_SPONSOR_TYPES;

  useEffect(() => {
    if (isOpen) {
      if (editingSponsor) {
        setSponsorTypeId(String(editingSponsor.sponsorTypeId || activeSponsorTypes[0]?.sponsorTypeId || 1));
        setName(editingSponsor.name || "");
        setLink(editingSponsor.link || "");

        if (editingSponsor.thumb instanceof File) {
          const url = URL.createObjectURL(editingSponsor.thumb);
          previewUrlRef.current = url;
          setPreviewImage(url);
          setThumb(editingSponsor.thumb);
        } else if (editingSponsor.thumbImageUrl) {
          setPreviewImage(editingSponsor.thumbImageUrl);
        } else {
          setPreviewImage(null);
          setThumb(null);
        }
      } else {
        setSponsorTypeId(String(activeSponsorTypes[0]?.sponsorTypeId || 1));
        setName("");
        setLink("");
        setThumb(null);
        setPreviewImage(null);
      }
      setValidationError("");
      setImageError("");
      setIsCropperOpen(false);
    }
  }, [editingSponsor, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setValidationError("Sponsor Brand Name is required.");
      return;
    }

    const matchedType = activeSponsorTypes.find((t) => String(t.sponsorTypeId) === sponsorTypeId);

    const sponsorItem: SponsorData = {
      id: editingSponsor?.id || `sponsor_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      sponsorTypeId: parseInt(sponsorTypeId, 10) || 1,
      sponsorType: matchedType,
      name: name.trim(),
      link: link.trim() || undefined,
      thumb,
      thumbImageKey: thumb?.name || editingSponsor?.thumbImageKey || "",
      thumbImageUrl: previewImage,
      isActive: editingSponsor?.isActive ?? true,
    };

    onSubmit(sponsorItem);
    toggle();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImageError("");
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setImageError("Only image files are allowed.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError("Max file size is 5 MB.");
      return;
    }

    setThumb(file);
    setFileToCrop(file);
    setIsCropperOpen(true);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.75)",
        backdropFilter: "blur(12px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={toggle}
    >
      <div
        className="glass-panel"
        style={{
          width: "100%",
          maxWidth: 520,
          background: "rgba(18, 20, 29, 0.94)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          borderRadius: 22,
          padding: 24,
          boxShadow: "0 24px 60px rgba(0,0,0,0.8)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Award size={18} color="#EE3F7C" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#fff" }}>
              {editingSponsor ? "Edit Brand Sponsor" : "Add New Brand Sponsor"}
            </h3>
          </div>
          <button
            type="button"
            onClick={toggle}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Validation Alert */}
          {validationError && (
            <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,93,115,0.15)", border: "1px solid #FF5D73", color: "#FF5D73", fontSize: 12, fontWeight: 600 }}>
              {validationError}
            </div>
          )}

          {/* Sponsor Tier / Type */}
          <div>
            <label className="v3-label">
              Sponsor Category / Tier <span style={{ color: "#EE3F7C" }}>*</span>
            </label>
            <select
              className="v3-select"
              value={sponsorTypeId}
              onChange={(e) => setSponsorTypeId(e.target.value)}
            >
              {activeSponsorTypes.map((t) => (
                <option key={t.sponsorTypeId} value={t.sponsorTypeId}>
                  {t.title}
                </option>
              ))}
            </select>
          </div>

          {/* Sponsor Brand Name */}
          <div>
            <label className="v3-label">
              Sponsor Brand Name <span style={{ color: "#EE3F7C" }}>*</span>
            </label>
            <input
              type="text"
              className="v3-input"
              placeholder="e.g. OpenAI / Red Bull"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (validationError) setValidationError("");
              }}
            />
          </div>

          {/* Website URL */}
          <div>
            <label className="v3-label">Official Website URL</label>
            <input
              type="url"
              className="v3-input"
              placeholder="https://sponsor.com"
              value={link}
              onChange={(e) => setLink(e.target.value)}
            />
          </div>

          {/* Sponsor Logo Upload with Cropper */}
          <div>
            <label className="v3-label">Sponsor Brand Logo</label>
            {previewImage && (
              <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
                <img
                  src={previewImage}
                  alt="Sponsor Logo Preview"
                  style={{ width: 64, height: 64, borderRadius: 10, objectFit: "contain", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", padding: 4 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setPreviewImage(null);
                    setThumb(null);
                  }}
                  style={{ background: "rgba(255,93,115,0.15)", border: "1px solid rgba(255,93,115,0.3)", color: "#FF5D73", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}
                >
                  Remove Logo
                </button>
              </div>
            )}
            <style>{`
              .sponsor-file-input {
                display: flex !important;
                align-items: center !important;
                height: 46px !important;
                padding: 4px 8px !important;
              }
              .sponsor-file-input::-webkit-file-upload-button,
              .sponsor-file-input::file-selector-button {
                margin: 0 12px 0 0 !important;
                border-radius: 6px !important;
                height: 34px !important;
                padding: 0 14px !important;
                display: inline-flex !important;
                align-items: center !important;
                justify-content: center !important;
                border: none !important;
                background: #ffffff !important;
                color: #222222 !important;
                font-weight: 500 !important;
                cursor: pointer !important;
              }
            `}</style>
            <input
              type="file"
              accept="image/*"
              className="sponsor-file-input v3-input"
              style={{ fontSize: 11 }}
              onChange={handleFileChange}
            />
            {imageError && <div style={{ fontSize: 11, color: "#FF5D73", marginTop: 4 }}>{imageError}</div>}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, marginTop: 10 }}>
            <button
              type="button"
              className="v3-btn-secondary"
              onClick={toggle}
              disabled={isLoading}
              style={{ padding: "8px 18px", fontSize: 12 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="v3-btn-primary"
              disabled={isLoading}
              style={{ padding: "8px 22px", fontSize: 12 }}
            >
              {editingSponsor ? "Update Sponsor" : "Save Sponsor"}
            </button>
          </div>
        </form>

        {/* Cropper Overlay */}
        {isCropperOpen && fileToCrop && (
          <ImageCropper
            imageFile={fileToCrop}
            aspect={1}
            onCropComplete={(croppedFile) => {
              setThumb(croppedFile);
              const url = URL.createObjectURL(croppedFile);
              if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
              previewUrlRef.current = url;
              setPreviewImage(url);
              setIsCropperOpen(false);
            }}
            onCancel={() => setIsCropperOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default NewSponsorModalV3;
